/* eslint-disable @typescript-eslint/no-non-null-assertion */
import createCachedSelector from 're-reselect'
import {createSelector} from 'reselect'
import {uniq, flatten} from 'ramda'
import {FeInputFieldStatusVal, FeInputFieldStatus, OnboardingActivationStatus} from '@modifi/enums'
import {AdminInputFieldView} from '@modifi/onboarding-api-clients'
import {
  getCombinedInputFieldStatus,
  getInputFieldStatus,
  selectInputFieldNames,
  selectInputFields,
  selectInputFieldsAreLoading,
} from '../../data/input-fields'
import {INPUT_FIELD_TO_VERIFIABLE_PARENT_MAPPING} from '../../constants/input-fields'
import {
  COMPANY_FINANCIAL_DOCUMENTS_SUBSECTIONS_INPUT_FIELD_MAP,
  COMPANY_DOCUMENTS_SUBSECTIONS_INPUT_FIELD_MAP,
} from '../../constants/section-to-input-fields'
import {getInputFieldsConfigBySection} from '../../utils/input-fields'
import {selectCompanyIsLocked, selectCompanyActivationStatus} from '../../data/company'

export const selectInputFieldsBySectionName = createCachedSelector(
  selectInputFields,
  (state: Record<string, any>, sectionName: string) => getInputFieldsConfigBySection(sectionName),
  (inputFields, inputFieldNames) =>
    // mapping through the inputFieldNames and use find instead of filtering inputFields
    // like below because ordering of inputFieldNames matter (for document upload pages)
    // inputFields.filter(i => inputFieldNames.includes(i.fieldName!))
    inputFieldNames
      .map(inputFieldName =>
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        inputFields.find(i => i.fieldName! === inputFieldName)
      )
      .filter(Boolean)
)((state, sectionName) => sectionName)

export const selectInputFieldNamesBySectionName = createSelector(
  selectInputFieldsBySectionName,
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  inputFields => inputFields.map(field => field!.fieldName) as string[]
)

const selectVerifiedInputFields = createSelector(
  selectInputFields,
  inputFields =>
    inputFields.reduce((result, inputField) => {
      const inputFieldName = inputField.fieldName!
      const parentInputFieldName = INPUT_FIELD_TO_VERIFIABLE_PARENT_MAPPING[inputFieldName]
      if (typeof parentInputFieldName !== 'undefined') {
        const parentInputField = inputFields.find(i => i.fieldName === parentInputFieldName)
        if (parentInputField && parentInputField.verified === true) {
          result.push(inputField)
        }
      } else if (inputField.verified === true) {
        result.push(inputField)
      }
      return result
    }, [] as AdminInputFieldView[]) as AdminInputFieldView[]
)

const selectVerifiedInputFieldNames = createSelector(
  selectVerifiedInputFields,
  verifiedInputFields => verifiedInputFields.map(i => i.fieldName!) as string[]
)

const selectVerifiableInputFieldsBySectionName = createCachedSelector(
  selectInputFields,
  (state: Record<string, any>, sectionName: string) => {
    const inputFieldNames = getInputFieldsConfigBySection(sectionName)
    return uniq(
      inputFieldNames.map(inputFieldName => {
        if (typeof INPUT_FIELD_TO_VERIFIABLE_PARENT_MAPPING[inputFieldName] !== 'undefined') {
          return INPUT_FIELD_TO_VERIFIABLE_PARENT_MAPPING[inputFieldName]
        }
        return inputFieldName
      })
    )
  },
  (inputFields, inputFieldNames) =>
    inputFields.filter(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      i => typeof i.verified !== 'undefined' && inputFieldNames.includes(i.fieldName!)
    )
)((state, sectionName) => sectionName)

export const selectCombinedInputFieldStatusBySectionName = createCachedSelector(
  selectInputFieldsAreLoading,
  selectVerifiableInputFieldsBySectionName,
  (isLoading, inputFields) => {
    if (isLoading && inputFields.length === 0) {
      return null
    }
    const statusList = inputFields
      .map(getInputFieldStatus)
      .filter(Boolean) as FeInputFieldStatusVal[]
    return getCombinedInputFieldStatus(statusList)
  }
)((state, sectionName) => sectionName)

export const selectListInputFieldStatusBySectionName = createCachedSelector(
  selectInputFieldsAreLoading,
  selectVerifiableInputFieldsBySectionName,
  (isLoading, inputFields) => {
    if (isLoading && inputFields.length === 0) {
      return null
    }
    return inputFields.map(getInputFieldStatus).filter(Boolean) as FeInputFieldStatusVal[]
  }
)((state, sectionName) => sectionName)

export const selectSectionProgress = createSelector(
  selectInputFieldsAreLoading,
  selectVerifiableInputFieldsBySectionName,
  (isLoading, inputFields) => {
    if (isLoading && inputFields.length === 0) {
      return 0
    }
    const statusList = inputFields
      .map(getInputFieldStatus)
      .filter(Boolean) as FeInputFieldStatusVal[]
    const totalItems = statusList.length
    const pendingItems = statusList.reduce((count, status) => {
      if (status === FeInputFieldStatus.PENDING) {
        return count + 1
      }
      return count
    }, 0)
    const progress = ((totalItems - pendingItems) / totalItems) * 100 || 0
    // rounding to nearest lower integer
    return Math.floor(progress)
  }
)

export const selectVisibleInputFieldNames = createSelector(
  selectInputFields,
  allInputFields => allInputFields?.map(inputField => inputField.fieldName!) || []
)

export const selectReadOnlyInputFieldNames = createSelector(
  selectInputFieldNames,
  selectVerifiedInputFieldNames,
  selectCompanyIsLocked,
  selectCompanyActivationStatus,
  (allInputFieldsNames, verifiedInputFieldNames, companyIsLocked, activationStatus) => {
    if (companyIsLocked) {
      return allInputFieldsNames
    }
    if (activationStatus !== OnboardingActivationStatus.ACTIVATED) {
      return verifiedInputFieldNames
    }
    const documentsInputFields = flatten([
      ...Object.values(COMPANY_FINANCIAL_DOCUMENTS_SUBSECTIONS_INPUT_FIELD_MAP),
      ...Object.values(COMPANY_DOCUMENTS_SUBSECTIONS_INPUT_FIELD_MAP),
    ])
    return verifiedInputFieldNames?.filter(
      inputField => !documentsInputFields?.includes(inputField)
    )
  }
)
